/**
 * Writing one filter set for two endpoints.
 *
 * The players endpoint takes flat query params (`ratingMin`, `gender: "male"`);
 * `/one-steps` takes a nested object (`rating: {min}`, `gender: ["male"]`) and
 * pulls the six face stats out under their own key, renaming one of them. Same
 * filter, same meaning, two spellings — so the translation lives here rather
 * than at either call site.
 */
import { categoryToUniqueKey } from "../playerAttributes";
/**
 * The face stats `/one-steps` wants under `faceStats` rather than with the
 * rest — the attribute taxonomy's categories, minus the keeper-only one.
 */
const FACE_STAT_KEYS = Object.values(categoryToUniqueKey).filter((key) => key !== "goalkeeping");
/** The one face stat whose filter key and API key disagree. */
const FACE_STAT_API_KEYS = {
    dribblingTitle: "dribbling",
};
/** Drops keys whose value is undefined, so an untouched filter sends nothing. */
const defined = (object) => Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined));
/**
 * The players endpoint's query params.
 *
 * `metaRatingMin`/`metaRatingMax` are deliberately dropped: the filter set can
 * carry them but `getPlayers` has no such parameter, so sending them would
 * quietly do nothing.
 */
export const toGetPlayersParams = (values) => {
    const { metaRatingMin: _metaRatingMin, metaRatingMax: _metaRatingMax, 
    // The endpoint calls this one `activeSquad`.
    mySquad, ...supported } = values.player;
    return defined({ ...supported, activeSquad: mySquad });
};
/**
 * `/one-steps`'s `filters` object.
 *
 * Every value describes the card the chain would produce, not the card as it
 * stands — a rating minimum of 90 keeps the steps that *reach* 90.
 */
export const toStepFilters = (values) => {
    const { player, evolution } = values;
    const filters = {};
    const rating = defined({ min: player.ratingMin, max: player.ratingMax });
    if (Object.keys(rating).length > 0)
        filters.rating = rating;
    if (player.quality?.length) {
        filters.quality = player.quality.map((q) => q.toLowerCase());
    }
    if (player.gender)
        filters.gender = [player.gender.toLowerCase()];
    if (player.skillMoves?.length)
        filters.skillMoves = player.skillMoves;
    if (player.weakFoot?.length)
        filters.weakFoot = player.weakFoot;
    if (player.positions?.length)
        filters.positions = player.positions;
    if (player.playStyles?.length)
        filters.playStyles = player.playStyles;
    if (player.playStylesPlus?.length) {
        filters.playStylesPlus = player.playStylesPlus;
    }
    if (player.accelerates?.length) {
        filters.accelerates = player.accelerates.map((a) => a.toLowerCase());
    }
    if (player.excludedPlayStyles?.length) {
        filters.excludedPlayStyles = player.excludedPlayStyles;
    }
    if (player.excludedPlayStylesPlus?.length) {
        filters.excludedPlayStylesPlus = player.excludedPlayStylesPlus;
    }
    if (player.maxPlayStyles !== undefined) {
        filters.maxPlayStyles = player.maxPlayStyles;
    }
    if (player.maxPlayStylesPlus !== undefined) {
        filters.maxPlayStylesPlus = player.maxPlayStylesPlus;
    }
    for (const [key, range] of Object.entries(player.inGameAttributes ?? {})) {
        if (range?.min === undefined && range?.max === undefined)
            continue;
        if (FACE_STAT_KEYS.includes(key)) {
            filters.faceStats ??= {};
            filters.faceStats[FACE_STAT_API_KEYS[key] ?? key] = { ...range };
        }
        else {
            filters.ingameAttributes ??= {};
            filters.ingameAttributes[key] = { ...range };
        }
    }
    if (evolution?.maxChainLength !== undefined) {
        filters.maxChainLength = evolution.maxChainLength;
    }
    if (evolution?.maxGames !== undefined)
        filters.maxGames = evolution.maxGames;
    return filters;
};
//# sourceMappingURL=adapters.js.map